import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import { adaptMeetings } from '@api/adapters/meetings';

import { TAG_MEETINGS } from '../../config/tags';
import { SCHEDULE_MEETINGS } from '../../config/urls';

import { baseQuery } from './baseQuery';

import type { Meeting } from '../types';
import type { HdeSchemas } from '../types/generated/apiSchemas';
import type { Lang } from '@assets/locales';

type MeetingsArgs = {
  startDate: string;
  endDate: string;
  language: Lang;
  page?: number;
  size?: number;
  sort?: string;
  audiences?: string[];
  specialities?: string[];
  countryCodes?: string[];
  racecourseCodes?: string[];
};

type MeetingsResponse = {
  meetings: Meeting[];
  totalCount: number;
  totalPages: number;
};

type AllMeetingsForFiltersArgs = {
  startDate: string;
  endDate: string;
  language: Lang;
  audiences?: string[];
  specialities?: string[];
  countryCodes?: string[];
};

const ALL_MEETINGS_PAGE_SIZE = 2000;

const extractPaginationHeaders = (meta: FetchBaseQueryMeta | undefined) => ({
  totalCount: Number(meta?.response?.headers.get('X-Total-Count')),
  totalPages: Number(meta?.response?.headers.get('X-Total-Page')) || 1,
});

// Define a service using a base URL and expected endpoints
export const meetingsApi = createApi({
  reducerPath: 'meetingsApi',
  tagTypes: [TAG_MEETINGS],
  baseQuery,
  refetchOnFocus: true,
  endpoints: builder => ({
    getMeetings: builder.query<MeetingsResponse, MeetingsArgs>({
      query: arg => ({
        url: SCHEDULE_MEETINGS,
        params: { ...arg },
      }),
      transformResponse: (
        response: HdeSchemas['Meeting'][],
        meta: FetchBaseQueryMeta
      ): MeetingsResponse => {
        const { totalCount, totalPages } = extractPaginationHeaders(meta);

        return {
          meetings: adaptMeetings(response),
          totalCount,
          totalPages,
        };
      },
    }),

    getAllMeetingsForFilters: builder.query<Meeting[], AllMeetingsForFiltersArgs>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const allMeetings: Meeting[] = [];
        let page = 0;
        let totalPages = 1;

        do {
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchWithBQ({
            url: SCHEDULE_MEETINGS,
            params: { ...arg, page, size: ALL_MEETINGS_PAGE_SIZE, sort: 'date,asc' },
          });

          if (result.error) {
            return { error: result.error };
          }

          const meta = result.meta as FetchBaseQueryMeta | undefined;
          const pagination = extractPaginationHeaders(meta);

          allMeetings.push(...adaptMeetings(result.data as HdeSchemas['Meeting'][]));
          totalPages = pagination.totalPages;
          page += 1;
        } while (page < totalPages);

        return { data: allMeetings };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetMeetingsQuery, useGetAllMeetingsForFiltersQuery } = meetingsApi;
